import React, { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { Spacing } from '@common/tokens';

/**
 * Semantic bulleted list that does not rely on browser default styles: list
 * styling is fully reset and the marker is recreated explicitly per item.
 * Compose item content freely — e.g. wrap it in `Text` for typography.
 */
export const List = ({
  children,
  className,
  'data-testid': dataTestId,
}: PropsWithChildren<{
  className?: string;
  'data-testid'?: string;
}>) => (
  <>
    {/* Explicit role: Safari drops list semantics when list-style is none. */}
    <ul
      role="list"
      className={clsx('wk-list', className)}
      data-testid={dataTestId}
    >
      {children}
    </ul>
    <style jsx>{`
      .wk-list {
        list-style: none;
        margin: 0;
        padding: 0;
        padding-left: ${Spacing.xs}px; /* Separate marker from content. */
        display: flex;
        flex-direction: column;
        gap: ${Spacing.xs}px;
      }
    `}</style>
  </>
);

const ListItem = ({
  children,
  className,
}: PropsWithChildren<{
  className?: string;
}>) => (
  <>
    <li className={clsx('wk-list__item', className)}>{children}</li>
    <style jsx>{`
      .wk-list__item {
        margin: 0;
        padding: 0;
        /* Marker and content as separate columns so wrapped lines align
           under the content rather than under the bullet. */
        display: flex;
        align-items: baseline;
      }
      /* Explicit marker instead of the browser's list-style bullet. */
      .wk-list__item::before {
        content: '•';
        margin-right: ${Spacing.sm}px;
        flex-shrink: 0;
      }
    `}</style>
  </>
);

List.Item = ListItem;
